import { dequal } from 'dequal';
import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Box, Grid, Typography, Divider, Button, IconButton } from '@material-ui/core';
import { FormGroup, FormControlLabel, FormLabel, FormControl, Checkbox, TextField } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/CancelSharp';
import mixpanel from 'mixpanel-browser';

import { AppContext } from './AppState';
import { DEFAULT_FILTERS, getAutoTags, getSelectedMats, useMaterials, useThumbnailUrls } from '../utils/materialsLib';
import { getNewOrder } from '../utils/tableLib';

import GalleryViewSelector from './GalleryViewSelector';
import GetStartedSplash from './GetStartedSplash';
import MaterialTable from './MaterialTable';
import MaterialGallery from './MaterialGallery';
import TagSelection from './TagSelection';
import { USER_TILING_INCLUDED } from '../utils/users';


// Columns to show in the list view
const tableColumns = [
  'name',
  'materialId',
  'createdAt',
  'text',
];

const gridSortKeys = tableColumns;

// Buttons to show in the list view
const tableButtons = [
  'stitchView',
  'report',
  'publish',
  'favourite',
  'delete',
];

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      flexWrap: 'nowrap',
    },
    width: '100%',
  },
  filterColumn: {
    marginRight: theme.spacing(2.5),
    minWidth: 120,
    maxWidth: 120,
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textDecorationThickness: '15%',
  },
  checkboxGroup: {
    marginTop: theme.spacing(1),
  },
  filterSummary: {
    color: '#777',
    fontWeight: 'light',
  },
  filterCheckboxField: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(-0.5),
  },
  filterCheckboxY: {
    marginLeft: theme.spacing(0.8),
    marginRight: theme.spacing(-0.5),
    marginTop: theme.spacing(-0.2),
    marginBottom: theme.spacing(0.2),
    padding: theme.spacing(0.3),
    '&:not(.Mui-checked)::before': {
      content: '"Y"',
      color: '#888',
      fontSize: '12px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  filterCheckboxN: {
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(-0.2),
    marginBottom: theme.spacing(0.2),
    padding: theme.spacing(0.3),
    '&:not(.Mui-checked)::before': {
      content: '"N"',
      color: '#888',
      fontSize: '12px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  filterCheckboxGroup: {
    display: 'flex',
  },
  filterTextField: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  filterInput: {
    //color: theme.palette.secondary.main,
  },
  filterButton: {
    width: '80px',
    float: 'right',
  },
  materialsContainer: {
    width: '100%',
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  closeButton: {
    color: 'grey',
    marginTop: theme.spacing(-0.5),
  },
}));


const FilterText = withStyles((theme) => ({
  root: {
    color: '#777',
    fontWeight: 'light',
    fontSize: '0.7em',
  }
}))(Typography);

function evaluateFilter(mat, filters, userTilingIncluded) {
  var matched = true;
  if (!filters.showStitchInputs) {
    matched &= !mat.isStitchInput;
  }
  if (filters.favourite) {
    matched &= mat.favourite;
  }
  if (filters.unlocked) {
    matched &= mat.unlocked;
  }
  if (filters.locked) {
    matched &= !mat.unlocked;
  }
  if (filters.tilingAccepted) {
    matched &= mat.tilingAccepted;
  }
  if (filters.tilingNotAccepted) {
    matched &= !mat.tilingAccepted;
  }
  if (filters.qa) {
    matched &= (mat.qaStatus === 'qa');
  }
  if (filters.noQa) {
    matched &= (mat.qaStatus !== 'qa');
  }
  if (filters.name.length > 0) {
    matched &= mat?.name?.toLowerCase().includes(filters.name.toLowerCase());
  }
  if (filters.description.length > 0) {
    matched &= mat?.text?.toLowerCase().includes(filters.description.toLowerCase());
  }
  const autoTags = [...getAutoTags(mat, mat.tiledUserPath, mat.tilingIncluded, userTilingIncluded).keys()];
  filters.tags.forEach(tag => {
    var tagMatch = false;
    if (mat['tags.user']) {
      tagMatch |= mat['tags.user'].includes(tag);
    }
    tagMatch |= autoTags.includes(tag);
    matched &= tagMatch;
  });
  filters.tagsExclude.forEach(tag => {
    if (mat['tags.user']) {
      matched &= !mat['tags.user'].includes(tag);
    }
    matched &= !autoTags.includes(tag);
  });

  return matched;
}

export default function Gallery(props) {
  const classes = useStyles();
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [folders, setFolders] = useState(['']);
  const {
    galleryMaterials, setGalleryMaterials,
    galleryViewMode, setGalleryViewMode,
    setGalleryFrom, setReportFrom,
    qaDisplay,
    retainFilters, setRetainFilters,
    retainFiltersExclude, setRetainFiltersExclude,
    filters, setFilters,
    filteredMaterials, setFilteredMaterials,
    folder, setFolder,
    order, setOrder,
    orderBy, setOrderBy,
    galleryUser,
    selected, setSelected,
    setUserTags,
    showAlert,
  } = useContext(AppContext);

  const {
    materials,
    displayMaterials,
    folders: foldersInit,
    tags,
    deleteMaterial,
    putFields,
    reloadMaterial,
    error,
  } = useMaterials(galleryUser, null, null, false, qaDisplay, false, 60000);

  const { thumbnailUrls } = useThumbnailUrls(galleryUser, materials, 'Gallery');

  useEffect(() => {
    if (error) {
      showAlert({ status: 'error', message: 'An error occurred while loading the materials' });
    }
  }, [error, showAlert]);

  useEffect(() => {
    if (displayMaterials) {
      setGalleryMaterials(displayMaterials);

      setGalleryFrom('/home');
      setReportFrom('/home');
    }
  }, [displayMaterials, setGalleryFrom, setGalleryMaterials, setReportFrom]);

  const showSplash = (displayMaterials?.length === 0);
  const showFilters = (displayMaterials?.length > 0);

  useEffect(() => {
    setFolders(f => foldersInit ?? f);
    if (folder !== '' && foldersInit && !foldersInit.includes(folder)) {
      changeFolder('');
    }
  }, [changeFolder, folder, foldersInit]);

  useEffect(() => {
    var uTags = [];
    tags.forEach(tag => {
      if (tag.tagType == 'user') {
        uTags.push({
          label: tag.label,
          tagType: tag.tagType
        });
      }
    });
    setUserTags(uTags);
  }, [tags, setUserTags]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  useEffect(() => {
    if (!filtersLoaded && displayMaterials) {
      // If the user is switching back to the gallery from certain pages (including view and report), then
      // we retain the filters that were previously applied last time they were in the gallery.
      if (retainFilters) {
        for (var exclude of retainFiltersExclude) {
          setFilters({ ...filters, [exclude]: false });
        }
        setRetainFiltersExclude([]);
        setRetainFilters(false);
      } else {
        resetFilters();
      }
      setFiltersLoaded(true);
    }
  }, [
    displayMaterials, filters, filtersLoaded, resetFilters, retainFilters, retainFiltersExclude, setFilters,
    setRetainFilters, setRetainFiltersExclude
  ]);

  useEffect(() => {
    // Remove any filters on tags for tags that no longer exist in the user's materials.
    setFilters(filters => {
      const newTagFilters = filters.tags.filter(t => tags.map(dt => dt.label).includes(t));
      const newFilters = { ...filters, tags: newTagFilters };
      if (!dequal(newFilters, filters)) {
        return { ...filters, tags: newTagFilters };
      } else {
        return filters;
      }
    })
  }, [tags, setFilters]);

  const onFilter = useCallback((matsToUse, filters) => {
    if (matsToUse) {
      // Filter by user filter options
      var filtMats = [];
      for (var i = 0; i < matsToUse.length; i++) {
        const m = matsToUse[i];
        if (evaluateFilter(m, filters, USER_TILING_INCLUDED)) {
          m.visible = true;
          filtMats.push(m);
        } else {
          m.visible = false;
        }
      }
      setFilteredMaterials(filtMats);
    }
  }, [setFilteredMaterials]);

  useEffect(() => {
    onFilter(galleryMaterials, filters);
  }, [filters, galleryMaterials, onFilter]);

  function countUnlocked(mats) {
    var count = 0;
    if (mats) {
      mats.forEach(m => {
        if (m.unlocked) {
          count += 1;
        }
      });
    }
    return count;
  }

  function filterSummaryText() {
    var parts = [];
    if (filters.favourite) {
      parts.push('Favourite=true');
    }
    if (filters.unlocked) {
      parts.push('Unlocked=true');
    }
    if (filters.locked) {
      parts.push('Unlocked=false');
    }
    if (filters.tilingAccepted) {
      parts.push('Tiling accepted=true');
    }
    if (filters.tilingNotAccepted) {
      parts.push('Tiling accepted=false');
    }
    if (filters.qa) {
      parts.push('QA=true');
    }
    if (filters.noQa) {
      parts.push('QA=false');
    }
    if (filters.showStitchInputs) {
      parts.push('Stitch Inputs=true');
    }
    if (filters.name) {
      parts.push(`Name containing '${filters.name.toLowerCase()}'`);
    }
    if (filters.description) {
      parts.push(`Description containing '${filters.description.toLowerCase()}'`);
    }
    if (filters.tags.length == 1) {
      parts.push(`Tag present: '${filters.tags}'`);
    } else if (filters.tags.length > 1) {
      parts.push(`All tags present: '${filters.tags}'`);
    }
    if (filters.tagsExclude.length == 1) {
      parts.push(`Tag excluded: '${filters.tagsExclude}'`);
    } else if (filters.tagsExclude.length > 1) {
      parts.push(`All tags excluded: '${filters.tagsExclude}'`);
    }

    var text;
    if (parts.length == 0) {
      text = 'All materials';
    } else {
      text = (<>
        Materials with:
        <ul>
          {parts.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </>);
    }
    return { isFiltered: parts.length > 0, text };
  }

  async function onSelectedTagsChanged(_materialId, tagsChanged) {
    const { selected: selectedTags, secondary } = tagsChanged;

    const mats = getSelectedMats(materials, selected);
    const materialsChanged = {};
    for (const mat of mats) {
      // Merge material's tags with the received tag change
      var newTagsList = mat['tags.user'] ? [...mat['tags.user']] : [];  // Start with a copy
      newTagsList.push(...(selectedTags ?? []));
      newTagsList = [...new Set(newTagsList)];  // Remove duplicates
      newTagsList = newTagsList.filter(t => ((selectedTags ?? []).includes(t) || (secondary ?? []).includes(t)));

      // Make an API call to update the material tags, if they have changed
      const orig = [...(mat['tags.user'] ?? [])];
      if (newTagsList.sort().join() !== orig.sort().join()) {
        materialsChanged[mat.materialId] = { tags: newTagsList };
      }
    }
    if (Object.keys(materialsChanged).length > 0) {
      putFields(materialsChanged);
    }
  }

  async function handleCheckbox(event) {
    var extraSettings = {};
    if (event.target.name == 'unlocked' && event.target.checked) {
      extraSettings = { 'locked': false }
    } else if (event.target.name == 'locked' && event.target.checked) {
      extraSettings = { 'unlocked': false }
    } else if (event.target.name == 'tilingAccepted' && event.target.checked) {
      extraSettings = { 'tilingNotAccepted': false }
    } else if (event.target.name == 'tilingNotAccepted' && event.target.checked) {
      extraSettings = { 'tilingAccepted': false }
    } else if (event.target.name == 'qa' && event.target.checked) {
      extraSettings = { 'noQa': false }
    } else if (event.target.name == 'noQa' && event.target.checked) {
      extraSettings = { 'qa': false }
    }
    setFilters({
      ...filters,
      [event.target.name]: event.target.checked,
      ...extraSettings
    });
  }

  async function handleValue(event) {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value
    });
  }

  function onTagSelection(val) {
    // filters may be received through the callback to ensure up-to-date state
    const { tags } = val;
    setFilters(filters => ({ ...filters, tags }));
  }

  const changeFolder = useCallback((newFolder) => {
    setFolder(folder => {
      if (newFolder !== folder) {
        return newFolder;
      } else {
        return folder;
      }
    });
  }, [setFolder]);

  async function onMoveFolder(target, mats) {
    mixpanel.track('Move materials', { 'Count': mats.length, 'Destination': target });

    const materialsChanged = {};
    for (const mat of mats) {
      materialsChanged[mat.materialId] = { folder: target };
    }

    if (Object.keys(materialsChanged).length > 0) {
      putFields(materialsChanged);
    }
  }

  function handleOrderByChange(event, element, property) {
    const newOrderBy = property ?? event.target.value;
    setOrder(getNewOrder(newOrderBy, order, orderBy));
    setOrderBy(newOrderBy);
  };

  const { isFiltered, text } = filterSummaryText();

  return (
    <Grid container className={classes.root}>
      <Grid item className={classes.filterColumn}>
        <Box pt={2} pb={2}>
          <GalleryViewSelector
            viewMode={galleryViewMode}
            setViewMode={setGalleryViewMode}
            />
        </Box>

        {showFilters && (<>
          <Typography variant='body2' className={classes.filterSummary}>
            Materials: {filteredMaterials.length}
          </Typography>
          <Typography variant='body2' className={classes.filterSummary}>
            Unlocked: {countUnlocked(filteredMaterials)}
          </Typography>

          <Divider className={classes.divider} />

          <Grid container className={classes.spaceBetween}>
            <Grid item>
              <Typography variant='h4'>
                Filters
              </Typography>
            </Grid>
            {isFiltered && (
              <Grid item>
                <IconButton size='small' variant='contained' onClick={resetFilters} className={classes.closeButton}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>

          <FormGroup className={classes.checkboxGroup}>
            <div className={classes.filterCheckboxGroup}>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.filterCheckboxY}
                    name='tilingAccepted'
                    checked={filters.tilingAccepted}
                    onChange={handleCheckbox}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.filterCheckboxN}
                    name='tilingNotAccepted'
                    checked={filters.tilingNotAccepted}
                    onChange={handleCheckbox}
                  />
                }
                label={<FilterText>Tiling accepted</FilterText>}
              />
            </div>
            <div className={classes.filterCheckboxGroup}>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.filterCheckboxY}
                    name='unlocked'
                    checked={filters.unlocked}
                    onChange={handleCheckbox}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.filterCheckboxN}
                    name='locked'
                    checked={filters.locked}
                    onChange={handleCheckbox}
                  />
                }
                label={<FilterText>Unlocked</FilterText>}
              />
            </div>
            {qaDisplay && (
              <div className={classes.filterCheckboxGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.filterCheckboxY}
                      name='qa'
                      checked={filters.qa}
                      onChange={handleCheckbox}
                    />
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.filterCheckboxN}
                      name='noQa'
                      checked={filters.noQa}
                      onChange={handleCheckbox}
                    />
                  }
                  label={<FilterText>QA</FilterText>}
                />
              </div>
            )}
            <FormControlLabel
              className={classes.filterCheckboxField}
              control={
                <Checkbox
                  name='favourite'
                  checked={filters.favourite}
                  onChange={handleCheckbox}
                />
              }
              label={<FilterText>Favourite</FilterText>}
            />
            <FormControlLabel
              className={classes.filterCheckboxField}
              control={
                <Checkbox
                  name='showStitchInputs'
                  checked={filters.showStitchInputs}
                  onChange={handleCheckbox}
                />
              }
              label={<FilterText>Show stitch inputs</FilterText>}
            />
            <TextField
              className={classes.filterTextField}
              InputLabelProps={{ shrink: true }}
              InputProps={{ classes: { input: classes.filterInput, }, }}
              name='name'
              label='Name'
              variant='outlined'
              size='small'
              value={filters.name}
              onChange={handleValue}
            />
            <TextField
              className={classes.filterTextField}
              InputLabelProps={{ shrink: true }}
              InputProps={{ classes: { input: classes.filterInput, }, }}
              name='description'
              label='Description'
              variant='outlined'
              size='small'
              value={filters.description}
              onChange={handleValue}
            />
          </FormGroup>
          <TagSelection
            tags={tags}
            selected={filters['tags']}
            setSelected={onTagSelection}
          />
          <Divider className={classes.divider} />
          <Typography className={classes.filterSummary} variant='caption'>
            {text}
          </Typography>
          {isFiltered && (
            <Grid container>
              <Grid item xs={12}>
                <Button
                  className={classes.filterButton}
                  variant='outlined'
                  color='secondary'
                  onClick={resetFilters}
                >
                  <FilterText>Clear</FilterText>
                </Button>
              </Grid>
            </Grid>
          )}
       </>)}
      </Grid>
      <Grid item className={classes.materialsContainer}>
        <Grid item xs={12}>
          {showSplash && <GetStartedSplash />}
          {galleryMaterials.length > 0 && galleryViewMode === 0 && (
            <MaterialGallery
              materials={galleryMaterials}
              deleteMaterial={deleteMaterial}
              putFields={putFields}
              reloadMaterial={reloadMaterial}
              onSelectedTagsChanged={onSelectedTagsChanged}
              folders={folders}
              folder={folder}
              setFolder={changeFolder}
              onMoveFolder={onMoveFolder}
              thumbnailUrls={thumbnailUrls}
              order={order}
              setOrder={setOrder}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              handleOrderByChange={handleOrderByChange}
              sortKeys={gridSortKeys}
            />
          )}
          {galleryMaterials.length > 0 && galleryViewMode === 1 && (
            <MaterialTable
              materials={galleryMaterials}
              columns={tableColumns}
              showTags={qaDisplay}
              buttons={tableButtons}
              deleteMaterial={deleteMaterial}
              putFields={putFields}
              reloadMaterial={reloadMaterial}
              onSelectedTagsChanged={onSelectedTagsChanged}
              folders={folders}
              folder={folder}
              filterByFolder={true}
              setFolder={changeFolder}
              onMoveFolder={onMoveFolder}
              allowSelection={true}
              showOnlyVisible={true}
              order={order}
              setOrder={setOrder}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              handleOrderByChange={handleOrderByChange}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
